import { styled } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import { Box, Typography, IconButton } from '@mui/material';

const chatPanelWidth = 350;

const openedMixin = (theme) => ({
  width: chatPanelWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: 0,
});

export const ChatDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  position: 'relative',
  width: chatPanelWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': {
      ...openedMixin(theme),
      background: '#ffffff',
      borderRight: '1px solid #E0E0E0',
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': {
      ...closedMixin(theme),
      background: '#ffffff',
    },
  }),
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

export const MainContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  paddingTop: '58px', // Account for main header height
  background: '#FAFAFA',
}));

export const Header = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 20px',
  borderBottom: '1px solid #F0F0F0',
  flexShrink: 0,
});

export const HeaderIcon = styled(Box)({
  width: 20,
  height: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const HeaderTitle = styled(Typography)({
  fontSize: '18px',
  fontWeight: 800,
  background:
    'linear-gradient(48.33deg, #7468FE 24.81%, #CE68FE 50.38%, #FE689E 76.45%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  textFillColor: 'transparent',
});

export const CloseButton = styled(IconButton)({
  color: '#666',
  '&:hover': {
    backgroundColor: '#F5F5F5',
  },
});

export const ContentArea = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

export const PreMessageContainer = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: '24px 20px 20px',
});

export const LoadingContainer = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
});

export const LoadingTitle = styled(Typography)({
  fontWeight: 600,
  color: '#374151',
  marginBottom: '8px',
});

export const LoadingSubtitle = styled(Typography)({
  fontSize: '14px',
  color: '#6B7280',
});

export const WelcomeContainer = styled(Box)({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const WelcomeTitle = styled(Typography)({
  fontSize: '22px',
  fontWeight: 700,
  color: '#424242',
  textAlign: 'center',
});

export const InputContainer = styled(Box)({
  marginBottom: '24px',
});

export const InputWrapper = styled(Box)({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
});

export const TextArea = styled('textarea')({
  width: '100%',
  minHeight: '48px',
  maxHeight: '120px',
  padding: '14px 50px 14px 16px',
  border: '2px solid #E0E0E0',
  borderRadius: '24px',
  fontSize: '14px',
  fontFamily: 'inherit',
  resize: 'none',
  outline: 'none',
  backgroundColor: '#FFFFFF',
  position: 'relative',
  '&::placeholder': {
    color: '#757575',
  },
  '&:focus': {
    border: '2px solid transparent',
    borderRadius: '24px',
    background:
      'linear-gradient(#FFFFFF, #FFFFFF) padding-box, linear-gradient(93.08deg, rgba(66, 229, 229, 0.4) 1.54%, rgba(102, 172, 254, 0.4) 24.92%, rgba(116, 104, 254, 0.4) 49.81%, rgba(206, 104, 254, 0.4) 74.2%, rgba(254, 104, 158, 0.4) 99.09%) border-box',
  },
});

export const SendButton = styled(IconButton)(({ disabled }) => ({
  position: 'absolute',
  right: '8px',
  width: '32px',
  height: '32px',
  backgroundColor: disabled ? '#D1D5DB' : '#6B46C1',
  color: disabled ? '#9CA3AF' : 'white',
  '&:hover': {
    backgroundColor: disabled ? '#D1D5DB' : '#553C9A',
  },
}));

export const SuggestionsTitle = styled(Typography)({
  fontSize: '12px',
  fontWeight: 700,
  color: '#424242',
  marginBottom: '16px',
});

export const SuggestionsContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

export const SuggestionItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  border: '1px dashed #D1D1D1',
  borderRadius: '12px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#F9FAFB',
    borderColor: '#D1D5DB',
  },
});

export const SuggestionText = styled(Typography)({
  fontSize: '12px',
  fontWeight: 600,
  color: '#212121',
  flex: 1,
  overflowWrap: 'break-word',
  whiteSpace: 'pre-wrap',
});

export const MessagesArea = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  padding: '16px 20px 0',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

export const MessageBubble = styled(Box)(({ messageType }) => ({
  maxWidth: '85%',
  width: 'fit-content',
  padding: '12px 16px',
  borderRadius: '12px',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  whiteSpace: 'pre-wrap',
  alignSelf: messageType === 'user' ? 'flex-end' : 'flex-start',
  backgroundColor: messageType === 'user' ? '#EEEEEE' : 'transparent',
  color: '#212121',
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 500,
}));

export const TypingIndicator = styled(Box)({
  maxWidth: '85%',
  padding: '12px 16px',
  borderRadius: '16px',
  alignSelf: 'flex-start',
  color: '#424242',
  fontSize: '14px',
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const TypingDots = styled(Box)({
  display: 'flex',
  gap: '2px',
  alignItems: 'center',
});

export const TypingDot = styled(Box)(({ delay }) => ({
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  backgroundColor: '#6B46C1',
  animation: 'pulse 1.4s ease-in-out infinite both',
  animationDelay: delay,
  '@keyframes pulse': {
    '0%, 80%, 100%': {
      transform: 'scale(0)',
    },
    '40%': {
      transform: 'scale(1)',
    },
  },
}));

export const TypingText = styled(Typography)({
  fontSize: 13,
  color: '#424242',
  marginLeft: 8,
  fontWeight: 500,
});

export const PostMessageInputContainer = styled(Box)({
  padding: '16px 20px 20px',
  borderTop: '1px solid #F0F0F0',
  flexShrink: 0,
});

export const DisclaimerText = styled(Typography)({
  fontSize: '12px',
  fontWeight: 500,
  color: '#616161',
  textAlign: 'center',
  display: 'block',
  marginTop: '16px',
  width: '100%',
  overflowWrap: 'break-word',
  whiteSpace: 'pre-wrap',
});
